import { useQuery } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { companyApi } from "@/lib/api/services/company.api";
import type { Company } from "@/types/company";

/**
 * An organization's concept list is short — a handful, not a catalogue — so the
 * whole thing is fetched once and filtered in the dropdown rather than paged
 * like {@link EntitySelect} does.
 */
const PAGE_SIZE = 100;

export interface ConceptMultiSelectProps {
  value: number[];
  onChange: (value: number[]) => void;
  organizationId?: number;
  /**
   * Names already in hand for ids the list may not carry — a concept defaulted
   * in by the caller, say. Anything still unnamed is resolved by id, so a
   * pre-selected concept never renders as a bare number (T-204).
   */
  displayNames?: Record<number, string>;
  placeholder?: string;
  disabled?: boolean;
  triggerClassName?: string;
}

/**
 * Pick one or more of an organization's concepts, as a dropdown of checkmarks.
 *
 * Multi-select because the thing being scoped is a menu item, which several
 * concepts in the same org can serve; the link lives on the item rather than
 * being read off its menu, since a menu shared org-wide names no concept at all.
 */
export function ConceptMultiSelect({
  value,
  onChange,
  organizationId,
  displayNames,
  placeholder,
  disabled,
  triggerClassName,
}: ConceptMultiSelectProps) {
  const { t } = useLingui();

  const { data } = useQuery({
    queryKey: ["concept-multi-select", organizationId ?? null],
    queryFn: () =>
      companyApi.paginate({ page: 1, limit: PAGE_SIZE, isActive: true, organizationId }),
    enabled: !disabled,
    staleTime: 5 * 60 * 1000,
  });
  const concepts: Company[] = data?.data ?? [];

  // A selected concept that is inactive, or simply on a page this query did not
  // ask for, is still selected — it just has no name here yet.
  const unnamed = value.filter(
    (id) => !concepts.some((c) => c.id === id) && !displayNames?.[id],
  );
  const { data: resolved } = useQuery({
    queryKey: ["concept-multi-select", "by-id", unnamed.join(",")],
    queryFn: () => Promise.all(unnamed.map((id) => companyApi.getById(id))),
    enabled: unnamed.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const nameOf = (id: number): string =>
    displayNames?.[id] ??
    concepts.find((c) => c.id === id)?.name ??
    resolved?.find((c) => c?.id === id)?.name ??
    String(id);

  const label = value.length > 0 ? value.map(nameOf).join(", ") : (placeholder ?? t`Select concepts`);

  const toggle = (id: number) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex h-10 w-full items-center justify-between gap-2 rounded-md border border-(--color-border) bg-(--color-surface-content) px-3 py-2 text-sm",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-ring) focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            value.length === 0 && "text-(--color-text-secondary)",
            triggerClassName,
          )}
        >
          <span className="truncate text-left">{label}</span>
          <ChevronDown className="size-4 shrink-0 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        // Radix measures the trigger, so the menu lines up with the field
        // instead of being as wide as the longest concept name.
        className="max-h-64 w-(--radix-dropdown-menu-trigger-width) overflow-y-auto"
      >
        {concepts.length === 0 ? (
          <div className="px-2 py-1.5 text-sm text-(--color-text-secondary)">
            {t`No concepts found`}
          </div>
        ) : (
          concepts.map((c) => (
            <DropdownMenuCheckboxItem
              key={c.id}
              checked={value.includes(c.id)}
              // Kept open: picking several concepts is the point, and closing
              // after each one would make that four trips through the menu.
              onSelect={(event) => event.preventDefault()}
              onCheckedChange={() => toggle(c.id)}
            >
              {c.name}
            </DropdownMenuCheckboxItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
